import facilitySiteService from "@/views/facilitySite/facilitySiteService";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { apiHosts } from "@/api/constants/apiHosts";
import type { CanDeleteFacilitySite, FacilitySite, FacilitySiteNew } from "@/views/facilitySite/facilitySite";
import type { AxiosResponse } from "axios";

const URL = "facility-sites";
const ENTITY_NAME = "Facility";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_SMART_API_BASE_URL,
};

const api = apiService<FacilitySite, Paging<FacilitySite>>(apiHosts.SMART, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getFacilitySites = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getFacilitySiteById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewFacilitySite = async () => {
  //optionally make any other changes as necessary before returning.
  return { data: facilitySiteService.getInitialData() } as AxiosResponse<FacilitySiteNew, any>;
};

const createFacilitySite = (facilitySite: FacilitySite, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, facilitySite, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateFacilitySite = (facilitySite: FacilitySite, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + facilitySite.id, facilitySite, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {FacilitySite} facilitySite This cannot be a 'ref' to a facilitySite
 */
const saveFacilitySite = (facilitySite: FacilitySite, config: PagingApiAndNotifierConfig = {}) => {
  if (facilitySite.id) {
    return updateFacilitySite(facilitySite, config);
  } else {
    return createFacilitySite(facilitySite, config);
  }
};

const deleteFacilitySiteById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteApi = apiService<CanDeleteFacilitySite, Paging<CanDeleteFacilitySite>>(apiHosts.SMART, axiosParams);
const canDeleteFacilitySite = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteFacilitySiteById,
  getFacilitySites,
  getFacilitySiteById,
  getNewFacilitySite,
  createFacilitySite,
  updateFacilitySite,
  saveFacilitySite,
  canDeleteFacilitySite,
};
